import { Field, Seg } from '../../ui/Block';
import { maxSlots, runnable, type BalancerConfig, type GpuType } from './engine';

const list = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean);

export function GpuGroups({ b, set }: { b: BalancerConfig; set: (b: BalancerConfig) => void }) {
  const gpus = (b.gpus ?? []).filter(runnable);
  const setGroup = (i: number, g: GpuType) => set({ ...b, gpuTypes: b.gpuTypes.map((x, j) => (j === i ? g : x)) });
  const members = (g: GpuType) => (g.gpus?.length ? g.gpus : [g.name]);
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="tbl__h">GPU GROUPS (in order; a create asks for any type of the group, cheapest first)</div>
      {b.gpuTypes.map((g, i) => {
        const ms = members(g);
        const mem = b.podMemory;
        const problems = ms.map((m) => {
          const t = (b.gpus ?? []).find((x) => x.name === m);
          if (!t) return `${m}: unknown`;
          if (!runnable(t)) return `${m}: not runnable`;
          if (t.slots < g.slots) return `${m} holds ${t.slots}${t.slotsMeasured ? '' : ' (estimate)'}`;
          if (mem && maxSlots(mem, t.deviceMb) < g.slots) return `${m}: memory fits ${maxSlots(mem, t.deviceMb)}`;
          return null;
        }).filter(Boolean);
        return (
          <div key={i} style={{ display: 'grid', gap: 8, padding: 10, border: '1px solid var(--fill-06)', borderRadius: 8 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label={`Group ${i + 1}`} value={g.name} onChange={(v) => setGroup(i, { ...g, name: v })} />
              <Field label="Sessions per pod" type="number" value={String(g.slots)} onChange={(v) => setGroup(i, { ...g, slots: Math.max(1, Math.min(9, Math.round(Number(v) || 1))) })} />
              {!g.gpus?.length && <Field label="$ per pod-hour" type="number" value={String(g.perH)} onChange={(v) => setGroup(i, { ...g, perH: Math.max(0, Number(v) || 0) })} />}
              <button type="button" className="btn btn--sm" disabled={i === 0} onClick={() => { const t = [...b.gpuTypes]; [t[i - 1], t[i]] = [t[i], t[i - 1]]; set({ ...b, gpuTypes: t }); }}>Up</button>
              <button type="button" className="btn btn--sm btn--danger" disabled={b.gpuTypes.length < 2} onClick={() => set({ ...b, gpuTypes: b.gpuTypes.filter((_, j) => j !== i) })}>Remove</button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {gpus.map((t) => {
                const on = ms.includes(t.name);
                const fits = t.slots >= g.slots;
                return (
                  <button key={t.name} type="button" className={`btn btn--xs ${on ? 'btn--primary' : ''}`} title={`${t.runpodId} · ${t.memoryGb} GB · sm ${t.sm} · $${t.perH.toFixed(2)}/h · holds ${t.slots}${t.slotsMeasured ? ' (measured)' : ' (estimate)'}`}
                    style={!fits && !on ? { opacity: 0.5 } : undefined}
                    onClick={() => setGroup(i, { ...g, gpus: on ? ms.filter((m) => m !== t.name) : [...ms.filter((m) => m !== g.name || g.gpus?.length), t.name] })}>
                    {t.name} · {t.memoryGb}G · ${t.perH.toFixed(2)}
                  </button>
                );
              })}
            </div>
            {problems.length > 0 && <span style={{ fontSize: 12, color: 'var(--conf-low)' }}>{problems.join(' · ')}: the settings check refuses this group.</span>}
          </div>
        );
      })}
      <div>
        <button type="button" className="btn btn--sm" onClick={() => set({ ...b, gpuTypes: [...b.gpuTypes, { name: `group ${b.gpuTypes.length + 1}`, perH: 0, slots: 7, diskGb: 60, gpus: [] }] })}>Add a group</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, alignItems: 'end' }}>
        <Field label="Near data centres" mono hint="asked first, in order; empty: anywhere" value={(b.dataCenters ?? []).join(',')} onChange={(v) => set({ ...b, dataCenters: list(v) })} />
        <Field label="Fallback data centres" mono hint="once the near ones refused a group" value={(b.fallbackDataCenters ?? []).join(',')} onChange={(v) => set({ ...b, fallbackDataCenters: list(v) })} />
        <Field label="Most round trip (ms)" type="number" hint="a data centre measured over it is not asked; 0 = off" value={String(b.maxRttMs ?? 0)} onChange={(v) => set({ ...b, maxRttMs: Math.max(0, Number(v) || 0) })} />
        <Field label="Refused: not asked for (s)" type="number" value={String(b.refusedCooldownS ?? 300)} onChange={(v) => set({ ...b, refusedCooldownS: Math.max(0, Number(v) || 0) })} />
        <Field label="Boot failures to leave a type out" type="number" hint={`within ${Math.round((b.badGpuWindowS ?? 3600) / 60)} min, for ${Math.round((b.badGpuCooldownS ?? 1800) / 60)} min`} value={String(b.badGpuFailures ?? 2)} onChange={(v) => set({ ...b, badGpuFailures: Math.max(1, Math.round(Number(v) || 1)) })} />
        <div className="field" style={{ margin: 0 }}>
          <span className="field__label">Which group</span>
          <Seg<'order' | 'cost'> value={b.gpuChoice ?? 'order'} onChange={(v) => set({ ...b, gpuChoice: v })} options={[{ value: 'order', label: 'In order' }, { value: 'cost', label: 'Cheapest per session' }]} />
        </div>
      </div>
    </div>
  );
}
